import { To, useLocation, useNavigate } from 'react-router-dom';

import airbridge from 'airbridge-web-sdk-loader';
import {
  FacebookAuthProvider,
  GoogleAuthProvider,
  OAuthCredential,
  OAuthProvider,
  User,
  signInWithCustomToken,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { t } from 'i18next';

import { HttpMethods } from 'api/httpMethods';
import { handleFailToast } from 'api/utils/errorHandler';
import { CreateUserRequestDto, getCustomToken, useCreateUser } from 'queryHooks';

import { endLoadingAndErrorToast } from './utils/auth';

export const useAuth = ({ auth, http }: { auth: any; http: HttpMethods }) => {
  const isLogin = window.localStorage.getItem('isCustomLogin') === 'true';
  const { state } = useLocation();
  const navigate = useNavigate();

  const { mutate: createUser } = useCreateUser(http);

  const checkLogin = (
    callbackFunction: Function,
    loginAlertModalOption?: {
      onClose?: () => void;
      closeOnBackdropClick?: boolean;
      callbackUrl?: string;
    },
    isStackModal?: boolean
  ) => {
    if (!isLogin) {
      return <></>;
      // return showLoginAlertModal(
      //   loginAlertModalOption?.onClose,
      //   loginAlertModalOption?.closeOnBackdropClick,
      //   loginAlertModalOption?.callbackUrl,
      //   isStackModal
      // );
    }
    callbackFunction();
  };

  const socialLogin = (
    providerConstructor: typeof GoogleAuthProvider | typeof FacebookAuthProvider | typeof OAuthProvider
  ) => {
    let provider;
    if (providerConstructor === OAuthProvider) {
      provider = new providerConstructor('apple.com');
    } else {
      provider = new providerConstructor();
    }

    signInWithPopup(auth, provider)
      .then((result) => {
        // setLoading(true);
        const credential = providerConstructor.credentialFromResult(result);
        const user = result.user;
        loginOrCreateUser(credential!, user);
      })
      .catch(() => {
        endLoadingAndErrorToast();
      });
  };

  const loginOrCreateUser = async (credential: OAuthCredential, user: User) => {
    const accessToken = credential.accessToken;

    // setTokenOnHeader(accessToken!);

    try {
      const res = await getCustomToken(http);
      const token = res?.token;

      if (token) await loginLastProcess(token);
      else endLoadingAndErrorToast();
    } catch (error: any) {
      const statusCode = error.response?.data?.statusCode;
      const isBlockedUser = error?.code === 'BLOCKED';
      const isNewUser = statusCode === 403 && !isBlockedUser;

      if (isNewUser) {
        // setLoading(false);
        await handleCreateUser(user);
      } else endLoadingAndErrorToast(error);
    }
  };

  const loginLastProcess = async (customToken: string) => {
    try {
      await signInWithCustomToken(auth, customToken);
      console.log('로그인 성공');
      // setLoading(false);
      // queryClient.invalidateQueries(MY_PROFILE_KEY);
      airbridge.events.send('airbridge.user.signin');
      localStorage.setItem('isCustomLogin', 'true');
      const redirectURL = localStorage.getItem('redirectURL');
      navigate((state as To) ?? redirectURL ?? '/');
      localStorage.removeItem('redirectURL');
    } catch (error) {
      endLoadingAndErrorToast(error);
      handleFailToast(t('login.account_fail_toast'));
    }
  };

  async function handleCreateUser(user: User) {
    const { uid, providerData } = user;
    const urlSearch = new URLSearchParams(window.location.search);
    const source = urlSearch.get('source');

    try {
      let reqBody = {
        externalId: uid,
        role: { code: 'USER' },
        displayLanguageCode: 'ko',
        storyLanguageCode: 'ko',
      } as CreateUserRequestDto;
      if (source) reqBody = { ...reqBody, source };
      // const { id: userId } = await api.users.userControllerCreateUser(reqBody);
      createUser(reqBody);
      // airbridge.events.send('airbridge.user.signup');

      const getProviderCode = (providerId: string) => {
        switch (providerId) {
          case 'apple.com':
            return 'APPLE';
          case 'google.com':
            return 'GOOGLE';
          default:
            return 'BUBBLETAP';
        }
      };
      // const providerId = providerData[0].providerId;
      // const providerCode = getProviderCode(providerId);
      // firebaseEvent.signUp({ userId, firebaseUserId: uid, providerCode });
      await logInAfterRegistered();
    } catch (error: any) {
      handleFailToast(t('general.contact_customer_support_error_message'));
    }
  }

  async function logInAfterRegistered() {
    try {
      const res = await getCustomToken(http);
      if (res?.token) loginLastProcess(res.token);
    } catch (err: any) {
      handleFailToast(t('general.contact_customer_support_error_message'));
    }
  }

  const logout = async (redirect: boolean = true, provider: string = '') => {
    // const provider = useUserDataStore.getState().provider;
    // const isCommunity = window.location.pathname.startsWith(BUBBLECHAT_WEB_PATH.WEB.COMMUNITY);
    // const isChat = window.location.pathname.startsWith(PATH.CHAT.BUBBLE_CHATS);

    try {
      if (provider === 'KAKAO') await window.Kakao.Auth.logout();
      await signOut(auth);

      document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      localStorage.removeItem('isCustomLogin');

      if (!airbridge.isSDKEnabled) {
        if (redirect) {
          window.location.assign('/');
          // if (isCommunity) window.location.assign(BUBBLECHAT_WEB_PATH.WEB.COMMUNITY);
          // else if (isChat) window.location.assign(PATH.CHAT.BUBBLE_CHATS);
          // else window.location.assign(PATH.CHAT.MAIN);
        }
        return;
      }

      airbridge.events.send('airbridge.user.signout');
      airbridge.events.wait(3000, () => {
        if (redirect) {
          window.location.assign('/');
          // if (isCommunity) window.location.assign(BUBBLECHAT_WEB_PATH.WEB.COMMUNITY);
          // else if (isChat) window.location.assign(PATH.CHAT.BUBBLE_CHATS);
          // else window.location.assign(PATH.CHAT.MAIN);
        }
        airbridge.clearUser();
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return { socialLoginWith: socialLogin, logout, checkLogin, loginLastProcess };
};
